import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { QRCodeSVG } from "qrcode.react";
import GoogleMapWidget from "./GoogleMapWidget";
import { fetchRSSFeed } from "../../utils/rssParser";
import { translateElement } from "./TraslateService";
import { speak, isSpeechSupported } from "./SpeechService";
import { ANIMATION_KEYFRAMES } from "./Utils/AnimationHelper";
import { animationCSS, bgStyleObj, buildGradientString, getLooseObjectValue, HIDE_SCROLLBAR_CSS, resolveDataValue, shadowCSS } from "./Utils/CssHelper";
import { buildYouTubeSrc, extractGoogleSheetId, fetchGoogleSheetRows } from "./Utils/leafHelpers";
import { defaultListFnConfig } from "./Utils/exports";
import { applyListFunctions, normalizeForCompare, normalizeListFnConfig, resolveValue } from "./Utils/DynamicValueResolver";
import { Clock } from "@mui/x-date-pickers/TimeClock/Clock";
import { LeafRender } from "./PreviewUtils/PreviewLeafRender";
import { GroupRender } from "./PreviewUtils/PreviewGroupRender";
import { ScreenRender } from "./PreviewUtils/PreviewScreenRender";
import { executeApiCall, executeDataSource, flattenNodes } from "./PreviewUtils/PreviewUtils";
import { CatalogContext } from "./Widgets/catalog";
import { KeyboardHost } from "./Widgets/OnScreenKeyboard";
import { IdlePrompt, homeScreenIdOf, useKioskIdle } from "./Widgets/KioskIdle";





export default function KioskPreview() {
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const [data, setData] = useState(null);
  const [activeScreenId, setActiveScreenId] = useState(null);
  const [vars, setVars] = useState({});
  const [apiStore, setApiStore] = useState({});
  const [rssStore, setRssStore] = useState({});
  const [globalDataStore, setGlobalDataStore] = useState({});
  const [visibilityOverrides, setVisibilityOverrides] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [manualZoom, setManualZoom] = useState(null);
  const [autoZoom, setAutoZoom] = useState(1);
  const [toast, setToast] = useState(null);
  const [showVars, setShowVars] = useState(false);
  const wrapRef = useRef(null);
  const activeScreenRef = useRef(null);
  const toastTimer = useRef(null);
  const sourceIntervalsRef = useRef([]);
  const varsRef = useRef({});
  useEffect(() => { varsRef.current = vars; }, [vars]);
  const zoom = manualZoom ?? autoZoom;

  useEffect(() => {
    const load = async () => {
      try {
        // Admin direct-data preview (no API call)
        const sessionRaw = sessionStorage.getItem("kioskPreviewRef");
        if (sessionRaw) {
          const sessionRef = JSON.parse(sessionRaw);
          if (sessionRef.directData) {
            const parsed = sessionRef.directData;
            setData(parsed);
            setActiveScreenId(homeScreenIdOf(parsed));
            setLoading(false);
            return;
          }
        }
        // Normal preview — read ref from localStorage, fetch fresh from API
        const refRaw = localStorage.getItem("kioskPreviewRef");
        if (!refRaw) { setError("This preview must be opened from ELM Studio."); setLoading(false); return; }
        const ref = JSON.parse(refRaw);
        const apiBase = localStorage.getItem("api");
        const token = localStorage.getItem("token") || sessionStorage.getItem("token");
        const reqBody = {
          path: "kioskTemplate/single",
          query: { id: ref.templateId },
          emmsId: ref.emmsId,
          body: {},
          requestType: "GET",
          headers: { "Content-Type": "application/json", "x-api-id": ref.apiId, "x-api-key": ref.apiKey },
        };
        const res = await fetch(apiBase + `/emms/piggyback`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify(reqBody),
        });
        const item = await res.json();
        const rd = item?.customProperties?.kioskReferenceData;
        if (!rd) { setError("No preview data found.\nSave the design in the editor first."); setLoading(false); return; }
        const parsed = typeof rd === "string" ? JSON.parse(rd) : rd;
        setData(parsed);
        setActiveScreenId(homeScreenIdOf(parsed));
        setLoading(false);
      } catch (e) { setError("Failed to load preview: " + e.message); setLoading(false); }
    };
    load();
  }, []);

  useEffect(() => {
    if (!wrapRef.current || !data) return;
    const screen = data.screens?.find(s => s.id === activeScreenId) || data.screens?.[0];
    if (!screen) return;
    const fit = () => {
      const { clientWidth: cw, clientHeight: ch } = wrapRef.current;
      setAutoZoom(Math.min((cw - 48) / screen.styles.width, (ch - 48) / screen.styles.height, 1));
    };
    fit();
    const obs = new ResizeObserver(fit); obs.observe(wrapRef.current); return () => obs.disconnect();
  }, [data, activeScreenId]);

  useEffect(() => {
    sourceIntervalsRef.current.forEach((id) => clearInterval(id));
    sourceIntervalsRef.current = [];
    if (!data) return;

    const sources = Array.isArray(data.dataSources) ? data.dataSources : [];
    const active = sources.filter((s) => s && s.enabled !== false);
    if (!active.length) return;

    const runSource = async (source) => {
      try {
        const result = await executeDataSource(source, varsRef.current);
        setGlobalDataStore((prev) => {
          const next = { ...prev, [source.id]: result };
          if (source.storeAs) next[source.storeAs] = result;
          return next;
        });
        if (source.storeAs) {
          setVars((prev) => ({ ...prev, [source.storeAs]: result }));
        }
      } catch (e) {
        console.warn(`Data source "${source.name || source.id}" failed:`, e.message);
      }
    };

    active.forEach((source) => {
      if (source.fetchOnScreenLoad !== false) {
        runSource(source);
      }
      if (source.pollEnabled) {
        const sec = Number(source.pollIntervalSec);
        const legacyMs = Number(source.pollIntervalMs);
        const hasValidSec = Number.isFinite(sec) && sec > 0;
        const hasValidLegacyMs = Number.isFinite(legacyMs) && legacyMs > 0;
        const interval = hasValidSec
          ? Math.max(1000, Math.round(sec * 1000))
          : hasValidLegacyMs
            ? Math.max(1000, legacyMs)
            : 60000;
        const id = setInterval(() => runSource(source), interval);
        sourceIntervalsRef.current.push(id);
      }
    });

    return () => {
      sourceIntervalsRef.current.forEach((id) => clearInterval(id));
      sourceIntervalsRef.current = [];
    };
  }, [data]);

  // Fetch RSS feeds from group-local RSS mode
  useEffect(() => {
    if (!data || !activeScreenId) return;
    const screen = data.screens?.find(s => s.id === activeScreenId);
    if (!screen) return;

    // Fetch RSS feeds from groups that have RSS data source enabled
    const fetchRSSFromGroups = (nodes) => {
      for (const node of nodes || []) {
        if (node.type === "group" && node.data?.mode === "rss" && node.data?.rssFeedUrl) {
          (async () => {
            try {
              const items = await fetchRSSFeed(node.data.rssFeedUrl);
              const limited = items.slice(0, node.data.rssMaxItems || 20);
              setRssStore(prev => ({ ...prev, [node.id]: limited }));
            } catch (e) {
              console.warn(`RSS feed "${node.data.rssFeedUrl}" failed:`, e.message);
            }
          })();
        }
        if (node.children) {
          fetchRSSFromGroups(node.children);
        }
      }
    };
    
    fetchRSSFromGroups(screen.children);

  }, [activeScreenId, data]);

  const onVarChange = useCallback((name, val) => setVars(prev => ({ ...prev, [name]: val })), []);
  const onHideModal = useCallback((id) => setVisibilityOverrides(prev => ({ ...prev, [id]: false })), []);

  const showToast = useCallback((msg, isError = false) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ msg, isError });
    toastTimer.current = setTimeout(() => setToast(null), 3000);
  }, []);

  const onAction = useCallback(async (actions, dataCtx) => {
    const queue = Array.isArray(actions) ? actions : [];
    let workingVars = { ...vars };
    let varsChanged = false;

    for (const act of queue) {
      if (act.type === "navigate") {
        if (varsChanged) {
          setVars(workingVars);
          varsChanged = false;
        }
        if (act.targetMode === "variable" && act.targetVar) {
          const nameOrId = workingVars[act.targetVar] ?? (dataCtx != null ? dataCtx[act.targetVar] : undefined);
          const target = data?.screens?.find(s => s.id === nameOrId || s.name === nameOrId);
          if (target) { setActiveScreenId(target.id); setApiStore({}); setVisibilityOverrides({}); }
        } else if (act.targetScreenId) {
          setActiveScreenId(act.targetScreenId); setApiStore({}); setVisibilityOverrides({});
        }
      } else if (act.type === "setVar") {
        let v = act.value || "";
        if (typeof v === "string" && v.startsWith("$")) {
          const key = v.slice(1);
          // Resolve from dataCtx (per-item data) first, then fall back to workingVars
          v = (dataCtx && key in dataCtx) ? dataCtx[key] : (workingVars[key] ?? "");
        } else if (typeof v === "string" && dataCtx && v in dataCtx) {
          // Also allow bare key names that exist in dataCtx (e.g. value="category")
          v = dataCtx[v];
        }
        if (act.varName) {
          workingVars = { ...workingVars, [act.varName]: v };
          varsChanged = true;
        }
      } else if (act.type === "apiCall") {
        try {
          const selectedSource = (data?.dataSources || []).find((s) => s.id === act.sourceId);
          const result = selectedSource
            ? await executeDataSource(selectedSource, workingVars)
            : await executeApiCall(act, workingVars);
          const storeKey = act.storeAs || selectedSource?.storeAs || "";
          if (storeKey) {
            workingVars = { ...workingVars, [storeKey]: result };
            varsChanged = true;
            setApiStore(prev => ({ ...prev, [storeKey]: result }));
            setGlobalDataStore(prev => ({ ...prev, [storeKey]: result }));
          }
          showToast(`✓ ${(selectedSource?.name || act.name || "API call")} succeeded`);
        } catch (e) { showToast(`✕ ${act.name || "API"} failed: ${e.message}`, true); }
      } else if (act.type === "showElements") {
        const ids = act.elementIds || [];
        if (ids.length > 0) {
          const screen = data?.screens?.find(s => s.id === activeScreenId) || data?.screens?.[0];
          const allNodes = flattenNodes(screen?.children || []);
          setVisibilityOverrides(prev => {
            const next = { ...prev };
            for (const id of ids) {
              const n = allNodes.find(x => x.id === id);
              const baseVisible = n ? n.visible !== false : true;
              const currentlyVisible = (baseVisible && prev[id] !== false) || prev[id] === true;
              next[id] = currentlyVisible ? false : true;
            }
            return next;
          });
        }
      }
    }

    if (varsChanged) {
      setVars(workingVars);
    }
  }, [vars, showToast, data, activeScreenId]);

  const homeId = homeScreenIdOf(data);
  const goHome = useCallback(() => {
    if (!homeId) return;
    setActiveScreenId(homeId);
    setVars({});
    setApiStore({});
    setVisibilityOverrides({});
  }, [homeId]);
  const idle = useKioskIdle({ settings: data?.settings, enabled: !!data, isHome: activeScreenId === homeId, onHome: goHome, screenKey: activeScreenId });

  if (isMobile) return <div style={{ height: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "#0e1418", color: "#8faab4", gap: 14, fontFamily: "system-ui,sans-serif", textAlign: "center", padding: 24 }}>
    <div style={{ width: 32, height: 32, borderRadius: 8, background: "#36454F", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 900, color: "#fff" }}>K</div>
    <div style={{ fontSize: 13, maxWidth: 280 }}>The Kiosk Preview is only supported on desktop. Please open this page on a larger screen.</div>
  </div>;

  if (loading) return <div style={{ height: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "#0e1418", color: "#6b8a96", gap: 12, fontFamily: "system-ui,sans-serif" }}>
    <div style={{ width: 32, height: 32, borderRadius: 8, background: "#36454F", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 900, color: "#fff" }}>K</div>
    <div style={{ fontSize: 13 }}>Loading preview…</div>
  </div>;

  if (error) return <div style={{ height: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "#0e1418", color: "#ef4444", fontFamily: "system-ui,sans-serif", textAlign: "center", padding: 40, gap: 16 }}>
    <div style={{ fontSize: 40 }}>⚠</div>
    <pre style={{ whiteSpace: "pre-wrap", lineHeight: 1.8, fontSize: 13, color: "#8faab4" }}>{error}</pre>
  </div>;

  const screens = data?.screens || [];
  const screen = screens.find(s => s.id === activeScreenId) || screens[0];
  const varEntries = Object.entries(vars).filter(([, v]) => typeof v === "string" || typeof v === "number");

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", background: "#0e1418", color: "#f0f4f5", fontFamily: "system-ui,sans-serif", overflow: "hidden" }}>
      <style>{HIDE_SCROLLBAR_CSS + ANIMATION_KEYFRAMES}</style>

      {/* Top bar */}
      <div style={{ height: 42, background: "#181f24", borderBottom: "1px solid #263038", display: "flex", alignItems: "center", padding: "0 12px", gap: 8, flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
          <div style={{ width: 22, height: 22, borderRadius: 5, background: "#36454F", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 900, color: "#fff" }}>K</div>
          <span style={{ fontSize: 11, fontWeight: 700 }}>KioskStudio</span>
          <span style={{ fontSize: 10, color: "#3d5560", background: "#1a2228", border: "1px solid #263038", borderRadius: 3, padding: "1px 5px" }}>Preview</span>
        </div>
        <div style={{ width: 1, height: 18, background: "#263038" }} />
        <div style={{ display: "flex", gap: 3, flex: 1, overflowX: "auto", minWidth: 0 }} className="kiosk-hs">
          {screens.map(sc => (
            <button key={sc.id} onClick={() => { setActiveScreenId(sc.id); setApiStore({}); }}
              style={{ background: activeScreenId === sc.id ? "#2a363d" : "transparent", border: `1px solid ${activeScreenId === sc.id ? "#36454F" : "transparent"}`, borderRadius: 4, color: activeScreenId === sc.id ? "#f0f4f5" : "#4a5f6b", cursor: "pointer", fontSize: 10, padding: "3px 10px", whiteSpace: "nowrap", flexShrink: 0, fontFamily: "inherit" }}>
              {sc.name}
            </button>
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 3, flexShrink: 0 }}>
          {[[null, "Fit"], [0.33, "33%"], [0.5, "50%"], [0.75, "75%"], [1, "100%"]].map(([z, l]) => (
            <button key={l} onClick={() => setManualZoom(z)}
              style={{ background: (z === null ? manualZoom === null : manualZoom === z) ? "#36454F" : "transparent", border: `1px solid ${(z === null ? manualZoom === null : manualZoom === z) ? "#4a5f6b" : "#263038"}`, borderRadius: 3, color: (z === null ? manualZoom === null : manualZoom === z) ? "#fff" : "#4a5f6b", cursor: "pointer", fontSize: 9, padding: "2px 6px", fontFamily: "inherit" }}>
              {l}
            </button>
          ))}
        </div>
        {varEntries.length > 0 && (
          <button onClick={() => setShowVars(v => !v)}
            style={{ background: showVars ? "#1e3444" : "transparent", border: `1px solid ${showVars ? "#6366f1" : "#263038"}`, borderRadius: 3, color: showVars ? "#818cf8" : "#4a5f6b", cursor: "pointer", fontSize: 9, padding: "2px 8px", flexShrink: 0, fontFamily: "inherit" }}>
            $ Vars ({varEntries.length})
          </button>
        )}
        <button onClick={() => window.close()} style={{ background: "transparent", border: "1px solid #263038", borderRadius: 3, color: "#4a5f6b", cursor: "pointer", fontSize: 9, padding: "2px 8px", flexShrink: 0, fontFamily: "inherit" }}>✕</button>
      </div>

      {showVars && varEntries.length > 0 && (
        <div style={{ background: "#111820", borderBottom: "1px solid #263038", padding: "6px 14px", display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", flexShrink: 0 }}>
          <span style={{ fontSize: 8, color: "#3d5560", textTransform: "uppercase", letterSpacing: "0.1em", flexShrink: 0 }}>Variables</span>
          {varEntries.map(([k, v]) => (
            <div key={k} style={{ display: "flex", gap: 3, alignItems: "center", background: "#1a2228", border: "1px solid #263038", borderRadius: 3, padding: "2px 6px" }}>
              <span style={{ color: "#6366f1", fontFamily: "monospace", fontSize: 9 }}>${k}</span>
              <span style={{ color: "#3d5560", fontSize: 9 }}>=</span>
              <span style={{ color: "#8faab4", fontFamily: "monospace", fontSize: 9, maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>"{String(v)}"</span>
            </div>
          ))}
          <button onClick={() => setVars({})} style={{ marginLeft: "auto", background: "none", border: "1px solid #263038", borderRadius: 3, color: "#3d5560", cursor: "pointer", fontSize: 8, padding: "2px 6px", fontFamily: "inherit" }}>Clear</button>
        </div>
      )}

      <div ref={wrapRef} style={{ flex: 1, overflow: "auto", display: "flex", alignItems: "center", justifyContent: "center", background: "#0e1418" }}>
        {screen
          ? <div style={{ position: "relative", width: screen.styles.width * zoom, height: screen.styles.height * zoom, flexShrink: 0 }}>
              <CatalogContext.Provider value={data?.catalog}>
                <KeyboardHost hasKeyboard={flattenNodes(screen.children || []).some(n => n.type === "keyboard")} vars={vars} onVarChange={onVarChange} resetKey={screen.id}>
                  <ScreenRender screen={screen} vars={vars} onVarChange={onVarChange} onAction={onAction} apiStore={apiStore} rssStore={rssStore} globalDataStore={globalDataStore} zoom={zoom} screenRef={activeScreenRef} visibilityOverrides={visibilityOverrides} onHideModal={onHideModal}/>
                </KeyboardHost>
              </CatalogContext.Provider>
              <IdlePrompt idle={idle} width={screen.styles.width} height={screen.styles.height} zoom={zoom} />
            </div>
          : <div style={{ color: "#2a363d", fontSize: 16 }}>No screens to display</div>}
      </div>

      {toast && (
        <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: toast.isError ? "#7f1d1d" : "#14532d", border: `1px solid ${toast.isError ? "#ef4444" : "#22c55e"}`, color: toast.isError ? "#fca5a5" : "#86efac", padding: "9px 18px", borderRadius: 6, fontSize: 12, fontWeight: 600, zIndex: 99999, pointerEvents: "none", boxShadow: "0 8px 32px rgba(0,0,0,0.6)" }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}